import { useState, useCallback } from "react";
import { useAuth } from "@/lib/auth-context";
import { useOrgSettings } from "@/lib/org-settings-context";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StatusFilter, ChipFilter, type StatusOption } from "@/components/status-filter";
import { IssueCardDialog } from "@/components/dialogs";
import { useToast } from "@/hooks/use-toast";
import { DetailCard, DetailGrid, DetailItem } from "@/components/detail-page";
import { ActivityTab } from "@/components/activity-tab";
import { VirtualCardVisual } from "@/components/virtual-card-visual";
import { getVariantFromId, type CardVariant } from "@/lib/card-designs";
import { EntityDrawer, useDrawerEntity, useDrawerSelect } from "@/components/entity-drawer";
import { cn } from "@/lib/utils";
import {
  CreditCard,
  Search,
  Plus,
  TrendingUp,
  Snowflake,
  XCircle,
  Play,
  Receipt,
  Pencil,
  Trash2,
  Loader2,
  Bot,
  RefreshCw,
  CheckCircle,
} from "lucide-react";

interface VirtualCard {
  id: string;
  customerId: string;
  customerName: string | null;
  customerEmail: string | null;
  walletId: string;
  externalCardId: string;
  lastFour: string | null;
  expMonth: number | null;
  expYear: number | null;
  cardBrand: string | null;
  cardType: string;
  status: string;
  nickname: string | null;
  singleTxnLimitCents: number | null;
  dailyLimitCents: number | null;
  monthlyLimitCents: number | null;
  dailySpentCents: number;
  monthlySpentCents: number;
  cardDesign: number | null;
  createdAt: string;
  updatedAt: string;
}

interface Transaction {
  id: string;
  type: string;
  amountCents: number;
  currency: string;
  status: string;
  merchantName: string | null;
  merchantCategory: string | null;
  createdAt: string;
}

function getStatusBadge(status: string) {
  const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    ACTIVE: "default",
    PENDING_APPROVAL: "outline",
    FROZEN: "secondary",
    CANCELLED: "destructive",
    EXPIRED: "outline",
  };
  const colors: Record<string, string> = {
    ACTIVE: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    PENDING_APPROVAL: "bg-amber-100 text-amber-800 border-amber-300",
    FROZEN: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  };
  const labels: Record<string, string> = {
    PENDING_APPROVAL: "Pending Approval",
  };
  return <Badge variant={variants[status] || "secondary"} className={colors[status]}>{labels[status] || status}</Badge>;
}

function getTxnStatusBadge(status: string) {
  const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    COMPLETED: "default",
    PENDING: "outline",
    DECLINED: "destructive",
    REVERSED: "secondary",
  };
  return <Badge variant={variants[status] || "secondary"}>{status}</Badge>;
}

const STATUS_OPTIONS: StatusOption[] = [
  { value: "ACTIVE", label: "Active" },
  { value: "FROZEN", label: "Frozen" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "EXPIRED", label: "Expired" },
];

const TYPE_OPTIONS: StatusOption[] = [
  { value: "VIRTUAL", label: "Virtual" },
];

// ─── Card Detail Content (drawer) ─────────────────────────────────────────

function CardDetailContent({ cardId, onClose }: { cardId: string; onClose: () => void }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const { formatCurrency } = useOrgSettings();
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<VirtualCard>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [balanceExpanded, setBalanceExpanded] = useState(false);

  // Single composite query replaces 2 separate API calls
  const { data: detail, isLoading } = useQuery<{
    card: VirtualCard;
    transactions: Transaction[];
    counts: { transactions: number };
  }>({
    queryKey: ["card-detail", cardId],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/cards/${cardId}/detail`);
      if (!response.ok) throw new Error("Failed to fetch card details");
      return response.json();
    },
    enabled: !!cardId,
  });

  const card = detail?.card;
  const transactions = detail?.transactions;

  // iServeU portal card balance (automation)
  const { data: balanceData, isFetching: balanceFetching, refetch: refetchBalance } = useQuery<{
    status: string;
    data: any;
  }>({
    queryKey: ["automation-card-balance", card?.customerId],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/admin/automation/card-balance?customer_id=${card?.customerId}`);
      if (!response.ok) throw new Error("Failed to fetch balance");
      return response.json();
    },
    enabled: false, // Only fetch when user clicks
    retry: false,
  });

  // iServeU portal card details (automation, SUPER only)
  const { data: detailsData, isFetching: detailsFetching, refetch: refetchDetails } = useQuery<{
    status: string;
    data: any;
  }>({
    queryKey: ["automation-card-details", card?.customerId],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/admin/automation/card-details?customer_id=${card?.customerId}`);
      if (!response.ok) throw new Error("Failed to fetch card details");
      return response.json();
    },
    enabled: false,
    retry: false,
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<VirtualCard>) => {
      const response = await apiRequest("PATCH", `/api/cards/${cardId}`, updates);
      if (!response.ok) throw new Error("Failed to update card");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["card-detail", cardId] });
      queryClient.invalidateQueries({ queryKey: ["cards"] });
      setIsEditing(false);
      toast({ title: "Success", description: "Card updated successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update card", variant: "destructive" });
    },
  });

  const freezeMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/cards/${cardId}/freeze`);
      if (!response.ok) throw new Error("Failed to freeze card");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["card-detail", cardId] });
      queryClient.invalidateQueries({ queryKey: ["cards"] });
      toast({ title: "Success", description: "Card frozen" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to freeze card", variant: "destructive" });
    },
  });

  const unfreezeMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/cards/${cardId}/unfreeze`);
      if (!response.ok) throw new Error("Failed to unfreeze card");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["card-detail", cardId] });
      queryClient.invalidateQueries({ queryKey: ["cards"] });
      toast({ title: "Success", description: "Card unfrozen" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to unfreeze card", variant: "destructive" });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("DELETE", `/api/cards/${cardId}`);
      if (!response.ok) throw new Error("Failed to cancel card");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cards"] });
      toast({ title: "Success", description: "Card cancelled" });
      onClose();
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to cancel card", variant: "destructive" });
    },
  });

  const activateMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/cards/${cardId}/activate`);
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || "Failed to activate card");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["card-detail", cardId] });
      queryClient.invalidateQueries({ queryKey: ["cards"] });
      toast({ title: "Card Activated", description: "Card is now active and ready to use." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const handleEdit = () => {
    if (card) {
      setEditForm({
        nickname: card.nickname || "",
        singleTxnLimitCents: card.singleTxnLimitCents,
        dailyLimitCents: card.dailyLimitCents,
        monthlyLimitCents: card.monthlyLimitCents,
      });
      setIsEditing(true);
    }
  };

  const handleSave = () => updateMutation.mutate(editForm);
  const handleCancelEdit = () => { setIsEditing(false); setEditForm({}); };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!card) return null;

  const dailyProgress = card.dailyLimitCents ? (card.dailySpentCents / card.dailyLimitCents) * 100 : 0;
  const monthlyProgress = card.monthlyLimitCents ? (card.monthlySpentCents / card.monthlyLimitCents) * 100 : 0;

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <CreditCard className="h-5 w-5 text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Card</span>
        </div>
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h2 className="text-xl font-semibold">{card.nickname || (card.status === "PENDING_APPROVAL" ? "Card ---- ----" : `Card **** ${card.lastFour}`)}</h2>
            {getStatusBadge(card.status)}
          </div>
          <p className="text-sm text-muted-foreground">
            {card.cardBrand?.toUpperCase() || "VISA"} {card.cardType}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {!isEditing && (
            <>
              {card.status === "PENDING_APPROVAL" && user?.role === "SUPER" && (
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => activateMutation.mutate()}
                  disabled={activateMutation.isPending}
                >
                  {activateMutation.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Activate Card
                </Button>
              )}
              {card.status === "ACTIVE" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => freezeMutation.mutate()}
                  disabled={freezeMutation.isPending}
                >
                  <Snowflake className="h-3.5 w-3.5 mr-1.5" />
                  Freeze
                </Button>
              )}
              {card.status === "FROZEN" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => unfreezeMutation.mutate()}
                  disabled={unfreezeMutation.isPending}
                >
                  <Play className="h-3.5 w-3.5 mr-1.5" />
                  Unfreeze
                </Button>
              )}
              {card.status !== "CANCELLED" && card.status !== "PENDING_APPROVAL" && (
                <>
                  <Button variant="outline" size="sm" onClick={handleEdit}>
                    <Pencil className="h-3.5 w-3.5 mr-1.5" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setDeleteDialogOpen(true)}
                    disabled={cancelMutation.isPending}
                  >
                    {cancelMutation.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Cancel Card
                  </Button>
                </>
              )}
            </>
          )}
        </div>

        {card.status === "PENDING_APPROVAL" && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-start gap-2.5">
            <CheckCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">Pending Approval</p>
              <p className="text-xs text-amber-600 mt-0.5">This card was requested by the customer and is awaiting admin approval before it can be used.</p>
            </div>
          </div>
        )}

        <div className="border-t" />

        {isEditing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label>Card Nickname</Label>
                <Input
                  value={editForm.nickname || ""}
                  onChange={(e) => setEditForm({ ...editForm, nickname: e.target.value })}
                  placeholder="e.g., Travel Card, Office Supplies"
                />
              </div>
              <div className="space-y-2">
                <Label>Single Transaction Limit (cents)</Label>
                <Input
                  type="number"
                  value={editForm.singleTxnLimitCents || ""}
                  onChange={(e) => setEditForm({ ...editForm, singleTxnLimitCents: parseInt(e.target.value) || null })}
                />
              </div>
              <div className="space-y-2">
                <Label>Daily Limit (cents)</Label>
                <Input
                  type="number"
                  value={editForm.dailyLimitCents || ""}
                  onChange={(e) => setEditForm({ ...editForm, dailyLimitCents: parseInt(e.target.value) || null })}
                />
              </div>
              <div className="space-y-2">
                <Label>Monthly Limit (cents)</Label>
                <Input
                  type="number"
                  value={editForm.monthlyLimitCents || ""}
                  onChange={(e) => setEditForm({ ...editForm, monthlyLimitCents: parseInt(e.target.value) || null })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={handleCancelEdit}>Cancel</Button>
              <Button onClick={handleSave} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        ) : (
          <Tabs defaultValue="overview" className="space-y-4">
            <TabsList className="w-full justify-start">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="transactions">Transactions ({transactions?.length || 0})</TabsTrigger>
              <TabsTrigger value="activity">Activity</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4">
              <DetailCard title="Your Card">
                <div className="flex justify-center py-4">
                  <VirtualCardVisual
                    lastFour={card.status === "PENDING_APPROVAL" ? "----" : card.lastFour}
                    cardholderName={card.nickname || "VEDA LOGIC CARDHOLDER"}
                    expMonth={card.status === "PENDING_APPROVAL" ? null : card.expMonth}
                    expYear={card.status === "PENDING_APPROVAL" ? null : card.expYear}
                    cardBrand={card.cardBrand}
                    status={card.status}
                    variant={(card.cardDesign as CardVariant) || getVariantFromId(card.id)}
                  />
                </div>
              </DetailCard>

              <DetailCard title="Card Information">
                <DetailGrid columns={1}>
                  <DetailItem label="Card Number" value={card.status === "PENDING_APPROVAL" ? "---- ---- ---- ----" : `**** **** **** ${card.lastFour || "****"}`} />
                  <DetailItem label="Nickname" value={card.nickname || "-"} />
                  <DetailItem label="Card Brand" value={<Badge variant="secondary">{card.cardBrand?.toUpperCase() || "VISA"}</Badge>} />
                  <DetailItem label="Card Type" value={<Badge variant="outline">{card.cardType}</Badge>} />
                  <DetailItem label="Expiry" value={card.expMonth && card.expYear ? `${card.expMonth}/${card.expYear}` : "-"} />
                  <DetailItem label="Status" value={getStatusBadge(card.status)} />
                </DetailGrid>
              </DetailCard>

              <DetailCard title="Spending Limits">
                <DetailGrid columns={1}>
                  <DetailItem label="Single Transaction" value={formatCurrency(card.singleTxnLimitCents)} />
                  <DetailItem label="Daily Limit" value={formatCurrency(card.dailyLimitCents)} />
                  <DetailItem label="Monthly Limit" value={formatCurrency(card.monthlyLimitCents)} />
                </DetailGrid>
              </DetailCard>

              <DetailCard title="Current Spending">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Daily Spent</span>
                      <span className={dailyProgress >= 100 ? "text-destructive font-medium" : ""}>
                        {formatCurrency(card.dailySpentCents)} / {formatCurrency(card.dailyLimitCents)}
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${dailyProgress >= 100 ? "bg-destructive" : dailyProgress >= 80 ? "bg-yellow-500" : "bg-primary"}`}
                        style={{ width: `${Math.min(dailyProgress, 100)}%` }}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Monthly Spent</span>
                      <span className={monthlyProgress >= 100 ? "text-destructive font-medium" : ""}>
                        {formatCurrency(card.monthlySpentCents)} / {formatCurrency(card.monthlyLimitCents)}
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${monthlyProgress >= 100 ? "bg-destructive" : monthlyProgress >= 80 ? "bg-yellow-500" : "bg-primary"}`}
                        style={{ width: `${Math.min(monthlyProgress, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </DetailCard>

              {/* iServeU Portal Balance (via automation) */}
              <DetailCard title={
                <div className="flex items-center justify-between w-full">
                  <span>Portal Card Balance</span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={balanceFetching}
                    onClick={() => refetchBalance()}
                  >
                    {balanceFetching ? (
                      <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                    ) : (
                      <Bot className="h-3 w-3 mr-1.5" />
                    )}
                    {balanceData ? "Refresh" : "Check Balance"}
                  </Button>
                </div>
              }>
                {balanceFetching && !balanceData ? (
                  <div className="flex flex-col items-center justify-center py-6 text-center">
                    <Loader2 className="h-6 w-6 animate-spin text-primary mb-2" />
                    <p className="text-xs text-muted-foreground">Checking balance via iServeU portal...</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">This may take 1–3 minutes</p>
                  </div>
                ) : balanceData?.status === "success" && balanceData.data ? (
                  <div className="space-y-3">
                    <DetailGrid columns={1}>
                      {(() => {
                        const details = balanceData.data.balance_details || balanceData.data;
                        const entries = Object.entries(details as Record<string, string>);
                        const shown = balanceExpanded ? entries : entries.slice(0, 6);
                        return shown.map(([key, value]) => (
                          <DetailItem key={key} label={key} value={String(value)} />
                        ));
                      })()}
                    </DetailGrid>
                    {Object.keys(balanceData.data.balance_details || balanceData.data).length > 6 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-xs text-muted-foreground"
                        onClick={() => setBalanceExpanded(!balanceExpanded)}
                      >
                        {balanceExpanded ? "Show less" : `Show all fields`}
                      </Button>
                    )}
                  </div>
                ) : balanceData?.status === "error" ? (
                  <p className="text-xs text-destructive py-2">
                    Failed to check balance. The automation bot may be offline.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground py-2">
                    Click "Check Balance" to retrieve the live card balance from the iServeU portal.
                  </p>
                )}
              </DetailCard>

              {/* iServeU Portal Card Details (via automation) — SUPER only */}
              {user?.role === "SUPER" && (
                <DetailCard title={
                  <div className="flex items-center justify-between w-full">
                    <span>Portal Card Details</span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      disabled={detailsFetching}
                      onClick={() => refetchDetails()}
                    >
                      {detailsFetching ? (
                        <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                      ) : (
                        <Bot className="h-3 w-3 mr-1.5" />
                      )}
                      {detailsData ? "Refresh" : "Fetch Details"}
                    </Button>
                  </div>
                }>
                  {detailsFetching && !detailsData ? (
                    <div className="flex flex-col items-center justify-center py-6 text-center">
                      <Loader2 className="h-6 w-6 animate-spin text-primary mb-2" />
                      <p className="text-xs text-muted-foreground">Fetching card details via iServeU portal...</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">This may take 1–3 minutes</p>
                    </div>
                  ) : detailsData?.status === "success" && detailsData.data ? (
                    <DetailGrid columns={1}>
                      {Object.entries((detailsData.data.card_details || detailsData.data) as Record<string, any>).map(([key, value]) => (
                        <DetailItem key={key} label={key} value={String(value)} />
                      ))}
                    </DetailGrid>
                  ) : detailsData?.status === "error" ? (
                    <p className="text-xs text-destructive py-2">
                      Failed to fetch card details. The automation bot may be offline.
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground py-2">
                      Click "Fetch Details" to retrieve live card details from the iServeU portal.
                    </p>
                  )}
                </DetailCard>
              )}

              <DetailCard title="Identifiers">
                <DetailGrid columns={1}>
                  <DetailItem label="Card ID" value={<code className="text-xs bg-muted px-2 py-1 rounded break-all">{card.id}</code>} />
                  <DetailItem label="External ID" value={<code className="text-xs bg-muted px-2 py-1 rounded break-all">{card.externalCardId}</code>} />
                  <DetailItem label="Customer ID" value={<code className="text-xs bg-muted px-2 py-1 rounded break-all">{card.customerId}</code>} />
                  <DetailItem label="Wallet ID" value={<code className="text-xs bg-muted px-2 py-1 rounded break-all">{card.walletId}</code>} />
                </DetailGrid>
              </DetailCard>

              <DetailCard title="Record Information">
                <DetailGrid columns={1}>
                  <DetailItem label="Created" value={new Date(card.createdAt).toLocaleString()} />
                  <DetailItem label="Last Updated" value={new Date(card.updatedAt).toLocaleString()} />
                </DetailGrid>
              </DetailCard>
            </TabsContent>

            <TabsContent value="transactions">
              <DetailCard title="Recent Transactions" description="Card transaction history">
                {transactions && transactions.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Merchant</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transactions.slice(0, 20).map((txn) => (
                          <TableRow key={txn.id}>
                            <TableCell>{new Date(txn.createdAt).toLocaleDateString()}</TableCell>
                            <TableCell>{txn.merchantName || "-"}</TableCell>
                            <TableCell>
                              {txn.merchantCategory && (
                                <Badge variant="outline">{txn.merchantCategory}</Badge>
                              )}
                            </TableCell>
                            <TableCell>{txn.type}</TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(txn.amountCents)}
                            </TableCell>
                            <TableCell>{getTxnStatusBadge(txn.status)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Receipt className="mx-auto h-12 w-12 text-muted-foreground" />
                    <p className="mt-2 text-muted-foreground">No transactions yet</p>
                  </div>
                )}
              </DetailCard>
            </TabsContent>

            <TabsContent value="activity">
              <ActivityTab
                entityType="VIRTUAL_CARD"
                entityId={card.id}
                createdAt={card.createdAt}
                updatedAt={card.updatedAt}
              />
            </TabsContent>
          </Tabs>
        )}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Card</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel card **** {card.lastFour}? This action cannot be undone and the card will no longer be usable.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => cancelMutation.mutate()} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Cancel Card
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function CustomerCards() {
  const { formatCurrency } = useOrgSettings();
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const [searchQuery, setSearchQuery] = useState("");
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const { drawerEntity, setDrawerEntity, displayEntity } = useDrawerEntity<string>();

  // Support ?select= for cross-entity navigation
  useDrawerSelect(setDrawerEntity);

  // Parse filters from URL
  const searchParams = new URLSearchParams(searchString);
  const urlStatus = searchParams.get("status");
  const urlType = searchParams.get("type");

  const [statusFilter, setStatusFilter] = useState<string | null>(urlStatus);
  const [typeFilter, setTypeFilter] = useState<string | null>(urlType);

  const { data: cards, isLoading } = useQuery<VirtualCard[]>({
    queryKey: ["cards"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/cards");
      if (!response.ok) throw new Error("Failed to fetch cards");
      return response.json();
    },
  });

  // Handle filter changes with URL sync
  const updateFilters = (status: string | null, type: string | null) => {
    const newParams = new URLSearchParams(searchString);
    if (status) {
      newParams.set("status", status);
    } else {
      newParams.delete("status");
    }
    if (type) {
      newParams.set("type", type);
    } else {
      newParams.delete("type");
    }
    newParams.delete("select");
    const newSearch = newParams.toString();
    setLocation(newSearch ? `?${newSearch}` : "", { replace: true });
  };

  const handleStatusChange = (status: string | null) => {
    setStatusFilter(status);
    updateFilters(status, typeFilter);
  };

  const handleTypeChange = (type: string | null) => {
    setTypeFilter(type);
    updateFilters(statusFilter, type);
  };

  const handleRowClick = useCallback((cardId: string) => {
    setDrawerEntity(prev => prev === cardId ? null : cardId);
  }, [setDrawerEntity]);

  // Filter cards
  const filteredCards = cards?.filter((c) => {
    const matchesSearch =
      c.lastFour?.includes(searchQuery) ||
      c.nickname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.cardType.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.customerName?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = !statusFilter || c.status === statusFilter;
    const matchesType = !typeFilter || c.cardType === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  // Calculate metrics
  const metrics = {
    total: cards?.length || 0,
    active: cards?.filter((c) => c.status === "ACTIVE").length || 0,
    frozen: cards?.filter((c) => c.status === "FROZEN").length || 0,
    cancelled: cards?.filter((c) => c.status === "CANCELLED").length || 0,
  };

  // Count by status for filter badges
  const statusCounts = cards?.reduce(
    (acc, c) => {
      acc[c.status] = (acc[c.status] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const typeCounts = cards?.reduce(
    (acc, c) => {
      acc[c.cardType] = (acc[c.cardType] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const statusOptionsWithCounts: StatusOption[] = STATUS_OPTIONS.map((opt) => ({
    ...opt,
    count: statusCounts?.[opt.value] || 0,
  }));

  const typeOptionsWithCounts: StatusOption[] = TYPE_OPTIONS.map((opt) => ({
    ...opt,
    count: typeCounts?.[opt.value] || 0,
  }));

  const metricCards = [
    {
      title: "Total Cards",
      value: metrics.total,
      icon: CreditCard,
      color: "text-primary",
      bgColor: "bg-primary/10",
    },
    {
      title: "Active",
      value: metrics.active,
      icon: TrendingUp,
      color: "text-green-600",
      bgColor: "bg-green-100 dark:bg-green-900/30",
    },
    {
      title: "Frozen",
      value: metrics.frozen,
      icon: Snowflake,
      color: "text-blue-600",
      bgColor: "bg-blue-100 dark:bg-blue-900/30",
    },
    {
      title: "Cancelled",
      value: metrics.cancelled,
      icon: XCircle,
      color: "text-red-600",
      bgColor: "bg-red-100 dark:bg-red-900/30",
    },
  ];

  return (
    <div className="relative min-h-screen">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-display font-bold tracking-tight">Cards</h1>
            <p className="text-muted-foreground">Manage all issued virtual cards</p>
          </div>
          <Button onClick={() => setIssueDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Issue Card
          </Button>
        </div>

        {/* Metrics Grid */}
        <div className="grid gap-4 md:grid-cols-4">
          {metricCards.map((metric) => (
            <Card key={metric.title} className="relative overflow-hidden">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className={cn("p-3 rounded-xl", metric.bgColor)}>
                    <metric.icon className={cn("h-5 w-5", metric.color)} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{metric.title}</p>
                    {isLoading ? (
                      <Skeleton className="h-8 w-16" />
                    ) : (
                      <p className="text-2xl font-display font-bold">{metric.value}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <StatusFilter
            value={statusFilter}
            onChange={handleStatusChange}
            options={statusOptionsWithCounts}
          />
          <div className="h-6 w-px bg-border" />
          <ChipFilter
            label="Type"
            value={typeFilter}
            onChange={handleTypeChange}
            options={typeOptionsWithCounts}
          />
        </div>

        {/* Cards Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>
                  {statusFilter || typeFilter
                    ? `Filtered Cards`
                    : "All Cards"}
                </CardTitle>
                <CardDescription>
                  {filteredCards?.length ?? 0} card{(filteredCards?.length ?? 0) !== 1 ? "s" : ""} found
                </CardDescription>
              </div>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by last 4, nickname, or customer..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : filteredCards?.length === 0 ? (
              <div className="text-center py-12">
                <CreditCard className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-semibold">No cards</h3>
                <p className="text-muted-foreground">
                  {statusFilter || typeFilter
                    ? "No cards match the selected filters."
                    : "No virtual cards have been issued yet."}
                </p>
                {!statusFilter && !typeFilter && (
                  <Button className="mt-4" onClick={() => setIssueDialogOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Issue Card
                  </Button>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Card</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Nickname</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Network</TableHead>
                    <TableHead>Expiry</TableHead>
                    <TableHead>Daily Limit</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCards?.map((card) => (
                    <TableRow
                      key={card.id}
                      className={cn(
                        "cursor-pointer hover:bg-muted/50",
                        drawerEntity === card.id && "bg-primary/10 hover:bg-primary/15"
                      )}
                      onClick={() => handleRowClick(card.id)}
                    >
                      <TableCell className="font-mono">
                        <div className="flex items-center gap-2">
                          <CreditCard className="h-4 w-4" />
                          {card.status === "PENDING_APPROVAL" ? "---- ----" : `**** ${card.lastFour || "****"}`}
                        </div>
                      </TableCell>
                      <TableCell>{card.customerName || "-"}</TableCell>
                      <TableCell>{card.nickname || "-"}</TableCell>
                      <TableCell><Badge variant="outline">{card.cardType}</Badge></TableCell>
                      <TableCell>
                        {card.cardBrand ? (
                          <Badge variant="secondary">{card.cardBrand}</Badge>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        {card.expMonth && card.expYear
                          ? `${String(card.expMonth).padStart(2, "0")}/${card.expYear}`
                          : "-"}
                      </TableCell>
                      <TableCell>{formatCurrency(card.dailyLimitCents)}</TableCell>
                      <TableCell>{getStatusBadge(card.status)}</TableCell>
                      <TableCell>{new Date(card.createdAt).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <IssueCardDialog
          open={issueDialogOpen}
          onOpenChange={setIssueDialogOpen}
        />
      </div>

      {/* Right-side detail drawer */}
      <EntityDrawer isOpen={!!drawerEntity} onClose={() => setDrawerEntity(null)}>
        {displayEntity && (
          <CardDetailContent key={displayEntity} cardId={displayEntity} onClose={() => setDrawerEntity(null)} />
        )}
      </EntityDrawer>
    </div>
  );
}
